import api, { API_ENDPOINTS } from "@/src/api/api";
import { config } from "@/src/config/config";

const BASE_URL_DSPM = config.API_URL + "/dspm_service";

export const dspmService = {
  getAllDataStores: async (
    page: number = 1,
    pageSize: number = 10,
    filter_by?: string,
    search?: string
  ) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}${API_ENDPOINTS.GET_ALL_DSPM_CONFIGURATIONS}`,
        {
          params: {
            page,
            page_size: pageSize,
            filter_by,
            search,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching data stores:", error);
      throw error;
    }
  },

  removeDataStore: async (config_id: string, reason: string) => {
    try {
      const response = await api.put(
        `${BASE_URL_DSPM}${API_ENDPOINTS.REMOVE_DSPM_DATA_STORE}`,
        {
          config_id: config_id,
          reason: reason
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting data store:", error);
      throw error;
    }
  },

  scanDataStore: async (data: any) => {
    try {
      const payload = {
        id: data?._id,
      };
      const response = await api.post(
        `${BASE_URL_DSPM}${API_ENDPOINTS.SCAN_DSPM_DATA_STORE}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error scanning data store:", error);
      throw error;
    }
  },
  getAllScanHistory: async (
    scanId: any,
    page: number = 1,
    pageSize: number = 10
  ) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}${API_ENDPOINTS.GET_DSPM_SCAN_HISTORY}/${scanId}`,
        {
          params: {
            page,
            page_size: pageSize,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching scan findings:", error);
      throw error;
    }
  },
  getAllFindings: async (
    scanId: any,
    page: number = 1,
    pageSize: number = 10,
    status?: string,
    severity?: string
  ) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}${API_ENDPOINTS.GET_ALL_DSPM_FINDINGS}/${scanId}`,
        {
          params: {
            page,
            page_size: pageSize,
            status,
            severity,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching scan findings:", error);
      throw error;
    }
  },
  getAllResources: async (
    scanId: any,
    page: number = 1,
    pageSize: number = 10
  ) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}${API_ENDPOINTS.GET_ALL_DSPM_RESOURCES}/${scanId}`,
        {
          params: {
            page,
            page_size: pageSize,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching scan findings:", error);
      throw error;
    }
  },
  getSingleResource: async (scanId: any, resourceId: any) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}${API_ENDPOINTS.GET_SINGLE_DSPM_RESOURCE}/${scanId}/${resourceId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching scan findings:", error);
      throw error;
    }
  },
  getAllCompliances: async (scanId: any) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}${API_ENDPOINTS.GET_ALL_DSPM_COMPLIANCES}/${scanId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching scan findings:", error);
      throw error;
    }
  },
  getSingleComplianceDetails: async (
    scanId: any,
    complianceId: any,
    page: number = 1,
    pageSize: number = 10
  ) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}${API_ENDPOINTS.GET_SINGLE_DSPM_COMPLIANCE_DETAILS}/${scanId}/${complianceId}`,
        {
          params: {
            page,
            page_size: pageSize,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching scan findings:", error);
      throw error;
    }
  },
  addDSPM: async (data: any) => {
    try {
      const response = await api.post(`${BASE_URL_DSPM}${API_ENDPOINTS.ADD_DSPM}`, data);
      return response.data;
    } catch (error) {
      console.error("Error adding data store:", error);
      throw error;
    }
  },
  getOverviewData: async () => {
    try {
      const response = await api.get(`${BASE_URL_DSPM}${API_ENDPOINTS.GET_DSPM_OVERVIEW_DATA}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching overview data:", error);
      throw error;
    }
  },
  rejectDSPMNotification: async (notificationId: string, reason: string) => {
    try {
      const response = await api.post(
        `${BASE_URL_DSPM}/dspm/deletion-requests/${notificationId}/reject`,
        {
          reason,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error rejecting notification:", error);
      throw error;
    }
  },
  approveDSPMNotification: async (notificationId: string, payload?: { export_zip: boolean }) => {
    const response = await api.post(
      `${BASE_URL_DSPM}/dspm/deletion-requests/${notificationId}/approve`,
      payload,
      {
        responseType: payload?.export_zip ? "arraybuffer" : "json", // ← key fix
      }
    );
    return response.data;
  },

  getDataExposure: async (id: string, page: number = 1, pageSize: number = 10) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}/dspm/data_exposure/${id}`,
        {
          params: {
            page,
            page_size: pageSize,
          },
          headers: { accept: "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching data exposure:", error);
      throw error;
    }
  },

  getDataClassificationStatus: async (id: string) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}/dspm/data_classification_status/${id}`,
        {
          headers: { accept: "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching data classification status:", error);
      throw error;
    }
  },

  getSensitiveDataLogs: async (id: string, page: number = 1, pageSize: number = 10) => {
    try {
      const response = await api.get(
        `${BASE_URL_DSPM}/dspm/sensitive_data_logs/${id}`,
        {
          params: {
            page,
            page_size: pageSize,
          },
          headers: { accept: "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching sensitive data logs:", error);
      throw error;
    }
  },
};
